import { useCallback } from 'react'
import type { Centrifuge, Subscription } from 'centrifuge'
import rootStore from '../Stores'
import { CHAT_PRESENCE_ENABLED } from '../Helpers/chatFeatures'
import { GetPresenceTokenTask } from '../Tasks/GetPresenceTokenTask'

/**
 * Повний онлайн-статус через спільний presence-канал орендаря.
 *
 * Вішається на ІСНУЮЧЕ Centrifugo-з'єднання (його володіє Ship
 * CentrifugoConnector — він викликає attachPresence після створення
 * клієнта): друга підписка, жодного другого сокета. Після підписки —
 * initial-знімок presence() («хто онлайн просто зараз»), далі живі
 * join/leave. Реконект безпечний: centrifuge-js повторює підписку сам,
 * 'subscribed' стріляє знову і свіжий знімок перекриває все пропущене
 * за час розриву.
 *
 * Presence некритичний: будь-який фейл тут мовчки деградує до «всі
 * офлайн», сам чат не зачіпається.
 */
export const useChatPresence = () => {
    const attachPresence = useCallback((client: Centrifuge): (() => void) => {
        if (!CHAT_PRESENCE_ENABLED) return () => {}

        let sub: Subscription | null = null
        let cancelled = false

        void (async () => {
            try {
                const { channel, token } = await GetPresenceTokenTask()
                if (cancelled) return

                sub = client.newSubscription(channel, {
                    token,
                    getToken: async () => (await GetPresenceTokenTask()).token,
                })

                sub.on('subscribed', () => {
                    void sub
                        ?.presence()
                        .then((result) => {
                            const uuids = Object.values(result.clients).map((info) => info.user)
                            rootStore.userStore.applyPresenceSnapshot(uuids)
                        })
                        .catch(() => {})
                })
                sub.on('join', (ctx) => rootStore.userStore.applyPresenceJoin(ctx.info.user))
                sub.on('leave', (ctx) => rootStore.userStore.applyPresenceLeave(ctx.info.user))

                sub.subscribe()
            } catch {
                // Токен не видався (мережа/сесія) — лишаємось без крапок
            }
        })()

        return () => {
            cancelled = true
            if (sub) {
                sub.unsubscribe()
                client.removeSubscription(sub)
            }
        }
    }, [])

    return { attachPresence }
}
